import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// MUI Components
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';

// Icons
import { ExportModalCIcon, PlusIconCIcon, InfoCIconFill } from '~/assets/images';

// Utils
import { SAMPLE_CSV } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { WarningIcon } from '~/assets/images';

// Actions
import { fetchContactLabels } from '~/redux/actions/contacts-actions';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';
import CreateTagModal from '~/components/Settings/Tags/CreateTag/index.jsx';
import TruncatedText from '~/components/General/TruncateTextFixedLength';
import LimitReach from '~/components/General/LimitModal';
import OlcButton from '~/components/General/OlcButton';

const DragDropUpload = ({
  onFileDrop,
  updateActiveTabe,
  updateSelectedFile,
  onTagsChange,
  isOrdersContext = false,
  onFileSelectedChange,
  processFileSignal,
  initialSelectedTags = [],
  initialSelectedTagIds = [],
  initialIsDncList = false,
  initialSelectedFile = [],
  dragActiveText = 'Drop the file here',
  defaultText = 'Select a File or drag and drop here',
  fileTypeText = 'CSV files only',
  buttonText = 'Select File',
  uploadButtonText = 'Uploading',
  downloadText = 'Download',
  sampleText = ' our sample CSV file here',
  requestId = null, // Add requestId prop to check for existing uploads
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({ status: false, message: '' });
  const [fileSelected, setFileSelected] = useState(initialSelectedFile.length > 0);
  const [selectedFileName, setSelectedFileName] = useState(
    initialSelectedFile.length > 0 ? initialSelectedFile[0]?.name || '' : ''
  );

  // Tags state
  const [selectedTags, setSelectedTags] = useState(initialSelectedTags);
  const [selectedTagIds, setSelectedTagIds] = useState(initialSelectedTagIds);
  const [createTagModal, setCreateTagModal] = useState(false);
  const [disableTags, setDisableTags] = useState(false);
  const [isDncList, setIsDncList] = useState(initialIsDncList);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  const tags = useSelector((state) => state.contactsReducers.labels);
  const sortedTags = useMemo(
    () => [...(tags || [])].sort((a, b) => a?.title.localeCompare(b?.title)),
    [tags]
  );
  const tagsOptions = useMemo(() => {
    const options = sortedTags
      ?.map((tag) => ({ id: tag.id, title: tag.title }))
      .concat({
        id: '-1',
        title: 'Create New Tag',
        custom: true,
        icon: PlusIconCIcon,
        func: () => setCreateTagModal(true),
      });
    return options;
  }, [sortedTags]);

  // Fetch tags on component mount
  useEffect(() => {
    dispatch(fetchContactLabels());
  }, []);

  // Update state when initial props change (when user goes back to step 0)
  useEffect(() => {
    setSelectedTags(initialSelectedTags);
    setSelectedTagIds(initialSelectedTagIds);
    setIsDncList(initialIsDncList);
    setFileSelected(initialSelectedFile.length > 0);
    setSelectedFileName(initialSelectedFile.length > 0 ? initialSelectedFile[0]?.name || '' : '');
    if (typeof onFileSelectedChange === 'function') {
      onFileSelectedChange(initialSelectedFile.length > 0);
    }
  }, [initialSelectedTags, initialSelectedTagIds, initialIsDncList, initialSelectedFile]);

  // Sync tag IDs when tagsOptions updates (e.g., after creating new tags)
  useEffect(() => {
    if (selectedTags.length > 0 && tagsOptions.length > 0) {
      const tagIdMap = new Map(
        tagsOptions.filter((t) => t.id !== '-1').map((t) => [t.title, t.id])
      );
      const newTagIds = selectedTags
        .map((tag) => {
          const tagTitle = typeof tag === 'string' ? tag : tag?.title;
          return tagIdMap.get(tagTitle);
        })
        .filter(Boolean);

      if (
        newTagIds.length !== selectedTagIds.length ||
        newTagIds.some((id, i) => id !== selectedTagIds[i])
      ) {
        setSelectedTagIds(newTagIds);
        onTagsChange?.({ selectedTags, selectedTagIds: newTagIds, isDncList });
      }
    } else if (selectedTags.length === 0 && selectedTagIds.length > 0) {
      setSelectedTagIds([]);
      onTagsChange?.({ selectedTags: [], selectedTagIds: [], isDncList });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsOptions, selectedTags]);

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTagSelectionChange = (newSelectedTags) => {
    setSelectedTags(newSelectedTags);
    const newTagIds = newSelectedTags
      .map((tagItem) => {
        const tagTitle = typeof tagItem === 'string' ? tagItem : tagItem?.title;
        const tag = tagsOptions.find((option) => option.title === tagTitle);
        return tag ? tag.id : null;
      })
      .filter((id) => id !== null && id !== -1);

    setSelectedTagIds(newTagIds);

    // Immediately call onTagsChange with the new data
    if (onTagsChange) {
      onTagsChange({
        selectedTags: newSelectedTags,
        selectedTagIds: newTagIds,
        isDncList,
      });
    }
  };

  const handleCreateTagModalUpdate = (updateFunction) => {
    if (typeof updateFunction === 'function') {
      const updated = updateFunction(selectedTags);
      setSelectedTags(updated);
      // useEffect will sync tag IDs when tagsOptions updates
    }
  };

  // Handle accepted file drop
  const onDropAccepted = (files) => {
    if (requestId) {
      setErrors((prev) => ({
        ...prev,
        status: true,
        message: MESSAGES.CONTACTS.IMPORT.UPLOAD_FILE.REPLACE,
      }));
      return;
    } else if (errors.status) {
      setErrors((prev) => ({
        ...prev,
        status: false,
      }));
    }

    try {
      // Set the selected file
      updateSelectedFile(files);
      setFileSelected(true);
      setSelectedFileName(files[0]?.name || '');
      if (typeof onFileSelectedChange === 'function') {
        onFileSelectedChange(true);
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        status: true,
        message: MESSAGES.CONTACTS.IMPORT.UPLOAD_FILE.INVALID_FILE,
      }));
    }
  };

  // Handle import button click
  const handleImportClick = () => {
    setIsUploading(true);

    try {
      // Ensure tags data is passed to parent before transitioning
      if (onTagsChange) {
        onTagsChange({
          selectedTags,
          selectedTagIds,
          isDncList,
        });
      }

      updateActiveTabe(1);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        status: true,
        message: MESSAGES.CONTACTS.IMPORT.UPLOAD_FILE.INVALID_FILE,
      }));
      setIsUploading(false);
    }
  };

  // Trigger import from parent (OrderFlow main footer)
  const lastSignalRef = useRef(processFileSignal);
  useEffect(() => {
    if (processFileSignal !== undefined && processFileSignal !== lastSignalRef.current) {
      lastSignalRef.current = processFileSignal;
      if (fileSelected && !isUploading) {
        handleImportClick();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processFileSignal]);

  // Handle cancel button click
  const handleCancelClick = () => {
    navigate(
      window?.location?.search?.includes('origin=contacts')
        ? '/contacts'
        : '/account/imports-exports'
    );
  };

  // Handle rejected file drop
  const onDropRejected = () => {
    setErrors((prev) => ({
      ...prev,
      status: true,
      message: MESSAGES.CONTACTS.IMPORT.UPLOAD_FILE.INVALID_FILE,
    }));
    return;
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    onDropAccepted,
    onDropRejected,
    maxFiles: 1,
    disabled: isUploading,
  });

  return (
    <Box
      className="dragDropContainerStep1"
      sx={{ py: 0, px: 2, display: 'flex', flexDirection: 'column' }}
    >
      <Box className="importStepScrollArea">
        {/* Dropzone Area */}
        <Box
          {...getRootProps()}
          className={`dropzoneArea olcDropzone ${isUploading ? 'uploading' : ''} ${isDragAccept && !isUploading ? 'dragActive' : ''} ${isDragReject || errors.status ? 'dragReject' : ''}`}
        >
          <input {...getInputProps()} />

          <Box className="dropzoneContent" sx={{ py: 3 }}>
            {/* Upload Icon */}
            <Box className="uploadIconContainer">
              <ExportModalCIcon fill="#00000099" />
            </Box>

            <Box className="textContent">
              <Typography className="mainText">{defaultText}</Typography>

              <Typography className="fileTypeText">{fileTypeText}</Typography>
            </Box>

            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexDirection: 'column',
              }}
            >
              <OlcButton variant="primary" disabled={isUploading}>
                {/* {isUploading ? (
              <Box className="loadingContainer">
                <CircularProgress size={16} thickness={5} color="inherit" />
                {uploadButtonText}
              </Box>
            ) : ( */}
                {buttonText}
                {/* )} */}
              </OlcButton>
              {selectedFileName ? (
                <TruncatedText
                  text={selectedFileName}
                  maxLength={30}
                  className="fileTypeText"
                  style={{ marginBottom: '15px' }}
                />
              ) : null}
            </Box>
          </Box>
        </Box>

        {/* Sample CSV Download Link */}
        <Box className="sampleDownloadContainer">
          <Typography className="sampleDownloadText">
            <Typography component="span" className="downloadLink" onClick={handleDownloadSample}>
              {downloadText}
            </Typography>{' '}
            {sampleText}
          </Typography>
        </Box>

        {/* Error Message */}
        {errors.status && (
          <Box className="errorMessageContainer">
            <Typography className="errorAlert alertText">
              <img
                src={WarningIcon}
                style={{ marginRight: '12px', height: '15px', width: '16.667px' }}
                alt="warning"
              />
              {errors.message}
            </Typography>
          </Box>
        )}

        {/* Tags Selection Section (hidden in Orders context) */}
        {/* {!isOrdersContext && ( */}
        <Box
          sx={{
            background: 'rgba(249, 250, 251, 1)',
            padding: '40px',
            borderRadius: '12px',
            // marginTop: '16px',
          }}
        >
          <div className="newImportContactTagsWrapper">
            <Box className="createContactInputImportContacts">
              <Box className="filterTagsWrapper">
                <Typography
                  style={{ marginBottom: '8px' }}
                  className="selectTagsHeaderTitleImportContacts"
                >
                  Select Tags
                </Typography>
                <MultiSelect
                  className="tag importContactsTags"
                  options={tagsOptions}
                  selectedValue={selectedTags}
                  setSelectedValue={handleTagSelectionChange}
                  placeHolderText={MESSAGES.CONTACTS.CREATE.CREATE_SEARCH_TAGS_PLACEHOLDER}
                  searchFieldPlaceholderText={
                    MESSAGES.CONTACTS.CREATE.CREATE_SEARCH_TAGS_PLACEHOLDER_IMPORT
                  }
                  disabled={disableTags}
                  clearable={!disableTags}
                  searchAutocomplete={true}
                  type="create"
                  setCreateTagModal={setCreateTagModal}
                  enabledSearch={true}
                  customIcon={false}
                />
              </Box>
            </Box>

            {/* DNC Checkbox */}
            {!isOrdersContext && (
              <Box
                className="dncListCheckWrapper"
                sx={{ marginBottom: '0px', fontWeight: '600', height: '22px', marginTop: '20px' }}
              >
                <FormControlLabel
                  className="returnAddressCheck"
                  control={
                    <Box className="checkboxRow ">
                      <Checkbox
                        className="bulkCheck newbulkCheck checkBoxSm"
                        icon={<Box className="bulkNotchecked" />}
                        checkedIcon={<DoneIcon />}
                        disableRipple={true}
                        checked={isDncList}
                        onChange={(e) => {
                          const newIsDncList = e.target.checked;
                          setIsDncList(newIsDncList);

                          // Immediately call onTagsChange with the updated DNC status
                          if (onTagsChange) {
                            onTagsChange({
                              selectedTags,
                              selectedTagIds,
                              isDncList: newIsDncList,
                            });
                          }
                        }}
                      />
                    </Box>
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="markText">Mark Contacts as Do Not Contact</span>
                      <Tooltip title={`Excludes these contacts from all future mailings.`}>
                        <img src={InfoCIconFill} alt="icon" />
                      </Tooltip>
                    </Box>
                  }
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            )}
          </div>
        </Box>
        {/* )} */}
      </Box>

      {!isOrdersContext && (
        <div
          className="finishFooter dragDropFooter importFlowFooter"
          style={{ marginTop: 'auto', justifyContent: 'flex-end' }}
        >
          <Box className="footerRight">
            <OlcButton variant="secondary" onClick={handleCancelClick}>
              Cancel
            </OlcButton>
            <OlcButton
              variant="primary"
              onClick={handleImportClick}
              disabled={!fileSelected || isUploading}
            >
              {isUploading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Importing
                </Box>
              ) : (
                'Next'
              )}
            </OlcButton>
          </Box>
        </div>
      )}

      {/* Create Tag Modal */}
      <CreateTagModal
        openModal={createTagModal}
        handleCloseModal={() => {
          setCreateTagModal(false);
          dispatch(fetchContactLabels());
        }}
        setSelectedTags={handleCreateTagModalUpdate}
        setTags={true}
        onLimitReached={(message) => {
          setCreateTagModal(false);
          setLimitMessage(message);
          setLimitModal(true);
        }}
      />
      {/* Limit Reached Modal */}
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="tags"
      />
    </Box>
  );
};

export default DragDropUpload;
